<template>
    <div class="table-responsive">
        <table class="table">
            <thead>
                <tr>
                    <th>
                        ชื่อสินค้า
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'product_name')"
                            :title="getSortTitle('product_name')"
                        >
                            <i :class="getSortIcon('product_name')"></i>
                        </button>
                    </th>
                    <th>
                        เริ่มนับ
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'expected_qty')"
                            :title="getSortTitle('expected_qty')"
                        >
                            <i :class="getSortIcon('expected_qty')"></i>
                        </button>
                    </th>
                    <th>
                        ขายไป
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'sold_qty')"
                            :title="getSortTitle('sold_qty')"
                        >
                            <i :class="getSortIcon('sold_qty')"></i>
                        </button>
                    </th>
                    <th>
                        นับได้
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'counted_qty')"
                            :title="getSortTitle('counted_qty')"
                        >
                            <i :class="getSortIcon('counted_qty')"></i>
                        </button>
                    </th>
                    <th>
                        ผลต่าง
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'difference')"
                            :title="getSortTitle('difference')"
                        >
                            <i :class="getSortIcon('difference')"></i>
                        </button>
                    </th>
                    <th>
                        สถานะ
                        <button
                            type="button"
                            class="btn btn-sm btn-link p-0 ms-1"
                            @click="$emit('toggle-sort', 'status')"
                            :title="getSortTitle('status')"
                        >
                            <i :class="getSortIcon('status')"></i>
                        </button>
                    </th>
                    <th>ค่าที่ตั้ง</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, i) in products" :key="item.id">
                    <td width="500px">
                        <div class="d-flex flex-column">
                            <span
                                style="display: block; max-height: 2.8em; overflow: hidden; word-wrap: break-word; white-space: normal; line-height: 1.4em;"
                            >
                                {{ item.product_name || '-' }}
                            </span>
                            <span v-if="item.selected_product_action_id">
                                {{ getActionLabel(item.selected_product_action_id) }}
                                <span v-if="item.latest_manage_at" class="text-muted">
                                    เมื่อ {{ format_thai_date(item.latest_manage_at, true) }}
                                    {{ item.latest_manage_by }}
                                </span>
                            </span>
                            <span v-else class="text-danger">ยังไม่แก้ไข</span>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span>{{ formatNumber(item.expected_qty, 0) }}</span>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column align-items-start">
                            <span>{{
                                formatNumber(
                                    item.sold_qty_before_count + item.sold_qty_after_count,
                                    0
                                )
                            }}</span>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span>{{ formatNumber(item.counted_qty, 0) }}</span>
                        </div>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span
                                :class="{
                                    'text-danger':
                                        item.counted_qty -
                                            item.expected_qty -
                                            (item.sold_qty_before_count + item.sold_qty_after_count) <
                                        0,
                                    'text-success':
                                        item.counted_qty -
                                            item.expected_qty -
                                            (item.sold_qty_before_count + item.sold_qty_after_count) >
                                        0,
                                }"
                            >
                                {{
                                    formatNumber(
                                        item.counted_qty -
                                            item.expected_qty -
                                            (item.sold_qty_before_count + item.sold_qty_after_count),
                                        0
                                    )
                                }}
                            </span>
                        </div>
                    </td>
                    <td>
                        <span
                            v-if="item.selected_product_action_id"
                            class="badge badge-success bg-success"
                            >จัดการแล้ว</span
                        >
                        <span v-else class="badge badge-warning bg-warning">มีปัญหา</span>
                    </td>
                    <td>
                        <div class="d-flex flex-column">
                            <span v-if="item.selected_product_action_id">{{
                                formatNumber(getProductQty(item), 0)
                            }}</span>
                            <span v-else class="text-muted">-</span>
                            <div class="mt-2">
                                <button
                                    class="btn btn-sm btn-alt-primary"
                                    @click="$emit('manage-lot', i)"
                                >
                                    จัดการ
                                </button>
                                <span
                                    v-if="item.has_pending_transactions"
                                    class="badge bg-info ms-1"
                                    :title="`มี Transaction ที่รอสร้าง ${item.pending_transactions?.length || 0} รายการ`"
                                >
                                    <i class="fas fa-clock"></i>
                                    {{ item.pending_transactions?.length || 0 }}
                                </span>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    name: 'StockCountProductTable',

    props: {
        products: {
            type: Array,
            default: () => [],
        },
        actions: {
            type: Array,
            default: () => [],
        },
        sortField: {
            type: String,
            default: null,
        },
        sortOrder: {
            type: String,
            default: null,
        },
    },

    methods: {
        getActionLabel(actionId) {
            const action = this.actions.find((a) => a.id === actionId);
            return action ? action.label : '';
        },

        getSortIcon(field) {
            if (this.sortField !== field) {
                return 'fas fa-sort text-muted';
            }
            return this.sortOrder === 'asc'
                ? 'fas fa-sort-up text-primary'
                : 'fas fa-sort-down text-primary';
        },

        getSortTitle(field) {
            if (this.sortField !== field) {
                return 'คลิกเพื่อเรียงลำดับ';
            }
            if (this.sortOrder === 'asc') {
                return 'เรียงจากน้อยไปมาก (คลิกเพื่อเรียงจากมากไปน้อย)';
            }
            return 'เรียงจากมากไปน้อย (คลิกเพื่อยกเลิกการเรียง)';
        },

        getProductQty(product) {
            let sumProduct = product.counted_qty;
            if (product.selected_product_action_id == 1) {
                sumProduct = product.expected_qty;
            } else if (product.selected_product_action_id == 2) {
                sumProduct =
                    product.expected_qty -
                    Number(product.sold_qty_before_count) +
                    Number(product.sold_qty_after_count);
            } else if (product.selected_product_action_id == 3) {
                sumProduct = product.counted_qty;
            } else if (product.selected_product_action_id == 4) {
                sumProduct = product.action_manual;
            } else if (product.selected_product_action_id == 5) {
                sumProduct =
                    product.counted_qty -
                    Number(product.sold_qty_before_count) +
                    Number(product.sold_qty_after_count);
            }
            return sumProduct;
        },

        formatNumber(n, dec = 2) {
            return new Intl.NumberFormat('us-US', {
                minimumFractionDigits: dec,
                maximumFractionDigits: dec,
            }).format(n);
        },

        format_thai_date(date, includeTime = false) {
            return window.format_thai_date(date, includeTime);
        },
    },
};
</script>
